import { GlobalStore, GridStore } from '@roc/feature-app-core';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { UnifiedLoanService } from '../../services/unifiedLoanService';
import { GENERIC_ERROR_MESSAGE } from 'libs/feature-utils/src/utils/constants';

const PAGE_SIZE = 10;
export class UnifiedMyLoansGridStore extends GridStore {
  globalStore: GlobalStore;
  unifiedLoanService: UnifiedLoanService;

  customStatuses: { name: string; ids: string[] }[] = [];

  private offsets: [number, number] = [0, 0];
  private totalCount: number = 0;

  constructor(globalStore: GlobalStore) {
    super(() => this.fetchUnifiedLoans(), undefined, PAGE_SIZE);
    this.globalStore = globalStore;
    this.unifiedLoanService = new UnifiedLoanService();
    makeObservable(this, {
      customStatuses: observable,
      fetchCustomStatuses: action,
    });
  }

  reset() {
    super.reset();
    this.offsets = [0, 0];
  }

  *setPageNumber(pageNumber, fetch = true): any {
    this.offsets = pageNumber === 1 ? [0, 0] : this.offsets;
    yield super.setPageNumber(pageNumber, fetch);
  }

  fetchCustomStatuses = async (lenderId: number) => {
    const response = await this.unifiedLoanService.getCustomStatuses(lenderId);
    runInAction(() => {
      this.customStatuses = response?.data?.data ?? [];
    });
  };

  fetchUnifiedLoans = async () => {
    try {
      const { capitalProvider, status, ...dropdownFilters } =
        this.gridData.meta.dropdownFilters || {};

      const selectedStatuses: string[] = status ? status.split(',') : [];

      const hasCustomStatuses = selectedStatuses.some(s => s.startsWith('CUSTOM_STATUS:'));

      const regularStatuses = [
        ...selectedStatuses.filter(s => !s.startsWith('CUSTOM_STATUS:')),
        ...(hasCustomStatuses ? ['CUSTOM_STATUS'] : []),
      ].join(',') || undefined;

      const customStatusNames = selectedStatuses
        .filter(s => s.startsWith('CUSTOM_STATUS:'))
        .map(s => s.replace('CUSTOM_STATUS:', ''));

      const lenderLoanStatusId =
        this.customStatuses
          .filter(cs => customStatusNames.includes(cs.name))
          .reduce((acc, cs) => acc.concat(cs.ids), [] as string[])
          .join(',') || undefined;

      const response = await this.unifiedLoanService.getMyLoans(
        this.gridData.meta.pageNumber,
        this.gridData.meta.pageSize,
        this.gridData.meta.sortDir,
        this.gridData.meta.sortBy,
        this.gridData.meta.filters,
        { ...dropdownFilters, lenderLoanStatusId },
        regularStatuses,
        this.offsets,
        capitalProvider
      );
      this.totalCount = response.data.data.totalCount;
      this.offsets = response.data?.data.offsets || [0, 0];
      response.data.data.totalCount = Math.min(
        this.totalCount,
        (this.offsets?.[0] || 0) +
          (this.offsets?.[1] || 0) +
          this.gridData.meta.pageSize
      );

      return response;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  };
}
